import { useState } from "react";
import { toast } from "sonner";
import { Beaker, Gauge, TestTubes, Stethoscope, Microscope } from "lucide-react";
import { optPts, gradeUsg, gradeDipstick } from "@/data/urinalysis";
import { BeatShell, OptionList, NumberField } from "@/components/nutrition/NutritionParts";
import { SampleTube, Refractometer, DipstickMatch } from "@/components/urinalysis/UrinalysisParts";
import { SedimentField, useSedimentField, UsgAzotaemiaPanel, CollectionMethodNote } from "@/components/urinalysis/SedimentScope";

/* ── Beat 1 · Gross appearance ────────────────────────────────────────────── */
export const GrossBeat = ({ c, onDone }) => {
  const [color, setColor] = useState(null);
  const [clarity, setClarity] = useState(null);
  const [res, setRes] = useState(null);
  const checked = !!res;

  const onPrimary = () => {
    if (!checked) {
      if (color == null || clarity == null) return toast.error("Pick a colour and a clarity.");
      const earned = optPts(c.gross.colorOptions[color].score, 10, 0) + optPts(c.gross.clarityOptions[clarity].score, 10, 0);
      setRes({ earned, max: 20, detail: "Colour & clarity", key: "gross", label: "Gross appearance" });
    } else onDone(res);
  };

  return (
    <BeatShell
      Icon={Beaker}
      eyebrow="Stage 1 of 4 · Appearance"
      title="Gross appearance"
      subtitle="Look at the specimen and record its colour and clarity. Tip: judge clarity by whether you can read the printed card through the tube."
      primaryLabel={checked ? "Continue →" : "Check answers"}
      onPrimary={onPrimary}
      primaryTestId="gross-primary-btn"
    >
      <div className="flex flex-col sm:flex-row gap-5">
        <div className="flex justify-center sm:justify-start shrink-0 bg-slate-50 rounded-lg border border-slate-200 p-3">
          <SampleTube colorKey={c.gross.colorKey} turbidity={c.gross.turbidity} />
        </div>
        <div className="flex-1 space-y-4">
          <div>
            <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold mb-1.5">Colour</div>
            <OptionList options={c.gross.colorOptions} value={color} onPick={setColor} revealed={checked} testidPrefix="gross-color" />
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold mb-1.5">Clarity</div>
            <OptionList options={c.gross.clarityOptions} value={clarity} onPick={setClarity} revealed={checked} testidPrefix="gross-clarity" />
          </div>
        </div>
      </div>
    </BeatShell>
  );
};

/* ── Beat 2 · Refractometer USG ───────────────────────────────────────────── */
export const RefractometerBeat = ({ c, onDone }) => {
  const [usg, setUsg] = useState("");
  const [cls, setCls] = useState(null);
  const [res, setRes] = useState(null);
  const checked = !!res;

  const onPrimary = () => {
    if (!checked) {
      if (usg === "") return toast.error("Read and enter the specific gravity.");
      if (cls == null) return toast.error("Classify the concentration.");
      const u = gradeUsg(usg, c.refract.usg);
      const earned = u.pts + optPts(c.refract.classifyOptions[cls].score, 5, 0);
      setRes({ uRes: u, scoreObj: { earned, max: 20, detail: `USG ${c.refract.usg.toFixed(3)}`, key: "refract", label: "Refractometer (USG)" } });
    } else onDone(res.scoreObj);
  };

  return (
    <BeatShell
      Icon={Gauge}
      eyebrow="Stage 2 of 4 · Refractometer"
      title="Specific gravity"
      subtitle="Read where the light/shadow boundary crosses the scale, then classify it."
      primaryLabel={checked ? "Continue →" : "Check answers"}
      onPrimary={onPrimary}
      primaryTestId="refract-primary-btn"
    >
      <div className="flex flex-col sm:flex-row gap-5">
        <div className="flex justify-center shrink-0 bg-slate-900 rounded-lg p-3">
          <Refractometer usg={c.refract.usg} />
        </div>
        <div className="flex-1 space-y-4">
          <NumberField
            testid="usg-input"
            label="Specific gravity (USG)"
            hint="e.g. 1.030 — read it off the boundary"
            value={usg}
            onChange={setUsg}
            disabled={checked}
            result={res?.uRes}
            correctText={c.refract.usg.toFixed(3)}
          />
          <div>
            <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold mb-1.5">Classify the concentration</div>
            <OptionList options={c.refract.classifyOptions} value={cls} onPick={setCls} revealed={checked} testidPrefix="refract-class" />
          </div>
        </div>
      </div>
      <UsgAzotaemiaPanel species={c.species} />
    </BeatShell>
  );
};

/* ── Beat 3 · Dipstick matching ───────────────────────────────────────────── */
export const DipstickBeat = ({ c, onDone }) => {
  const [sel, setSel] = useState({});
  const [res, setRes] = useState(null);
  const checked = !!res;

  const select = (key, i) => setSel((s) => ({ ...s, [key]: i }));

  const onPrimary = () => {
    if (!checked) {
      if (Object.keys(sel).length < c.dipstick.length) return toast.error("Match every pad to the chart.");
      const g = gradeDipstick(c.dipstick, sel);
      setRes({ earned: g.pts, max: 30, detail: `${g.correctCount}/${g.total} pads read correctly`, key: "dipstick", label: "Reagent dipstick" });
    } else onDone(res);
  };

  return (
    <BeatShell
      Icon={TestTubes}
      eyebrow="Stage 3 of 4 · Dipstick"
      title="Reagent dipstick reading"
      subtitle="For each pad, click the chart swatch that matches its colour."
      primaryLabel={checked ? "Continue →" : "Check readings"}
      onPrimary={onPrimary}
      primaryTestId="dipstick-primary-btn"
    >
      <DipstickMatch rows={c.dipstick} selected={sel} onSelect={select} revealed={checked} />
    </BeatShell>
  );
};

/* ── Beat 4 · Sediment microscopy ─────────────────────────────────────────── */
export const SedimentBeat = ({ c, onDone }) => {
  const layout = useSedimentField();
  const [answers, setAnswers] = useState({});
  const [res, setRes] = useState(null);
  const checked = !!res;
  const answer = (kindId, L) => { if (checked) return; setAnswers((a) => ({ ...a, [kindId]: L })); };

  const onPrimary = () => {
    if (!checked) {
      if (Object.keys(answers).length < 4) return toast.error("Identify all four sediment findings.");
      const LETTERS = ["A", "B", "C", "D"];
      let correct = 0;
      layout.forEach((k, i) => { if (answers[k.id] === LETTERS[i]) correct += 1; });
      setRes({ earned: correct * 5, max: 20, detail: `${correct}/4 sediment findings located`, key: "sediment", label: "Sediment exam" });
    } else onDone(res);
  };

  return (
    <BeatShell
      Icon={Microscope}
      eyebrow="Stage 4 of 5 · Sediment"
      title="Examine the spun sediment"
      subtitle="Spin the sample, then identify the formed elements under the microscope — match each finding to the correct field (A–D)."
      primaryLabel={checked ? "Continue →" : "Check sediment"}
      onPrimary={onPrimary}
      primaryTestId="sediment-primary-btn"
    >
      <SedimentField layout={layout} answers={answers} onAnswer={answer} revealed={checked} />
    </BeatShell>
  );
};

/* ── Beat 5 · Interpretation + overview ───────────────────────────────────── */
export const DiagnosisBeat = ({ c, onDone }) => {
  const [idx, setIdx] = useState(null);
  const [res, setRes] = useState(null);
  const checked = !!res;

  const onPrimary = () => {
    if (!checked) {
      if (idx == null) return toast.error("Choose your interpretation.");
      setRes({ earned: optPts(c.diagnosis.options[idx].score, 30, 15), max: 30, detail: "Interpretation", key: "diagnosis", label: "Interpretation" });
    } else onDone(res);
  };

  return (
    <BeatShell
      Icon={Stethoscope}
      eyebrow="Stage 5 of 5 · Interpretation"
      title="What does it all mean?"
      subtitle={c.diagnosis.prompt}
      primaryLabel={checked ? "See debrief →" : "Check answer"}
      onPrimary={onPrimary}
      primaryTestId="diagnosis-primary-btn"
    >
      <CollectionMethodNote />
      <OptionList options={c.diagnosis.options} value={idx} onPick={setIdx} revealed={checked} testidPrefix="diagnosis-option" />
      {checked && (
        <div className="rounded-lg bg-[#b45309]/5 border border-[#b45309]/20 p-4" data-testid="diagnosis-overview">
          <div className="text-[11px] uppercase tracking-widest text-[#b45309] font-semibold mb-1">What this looks like — and why</div>
          <p className="text-sm text-slate-700 leading-relaxed">{c.diagnosis.overview}</p>
        </div>
      )}
    </BeatShell>
  );
};
